import { useState, useEffect, useCallback, useRef } from 'react';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];

function fmtFecha(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

async function verDoc(d) {
  try {
    const blob = await api.get(`/api/documents/${d.id}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { alert('No se pudo abrir el prejob'); }
}

async function descargarDoc(d) {
  try {
    const blob = await api.get(`/api/documents/${d.id}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = d.file_name || `${d.nombre || 'prejob'}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch { alert('No se pudo descargar el prejob'); }
}

export default function PrejobSegavan() {
  const user = useAuthStore(s => s.user);
  const isAdmin = ROLES_ADMIN.includes(user?.rol);
  const [histOpen, setHistOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const loadHistorial = useCallback(() => {
    setLoading(true);
    api.get('/api/documents/prejobs-mios')
      .then(r => setDocs(r.data || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (histOpen) loadHistorial();
  }, [histOpen, loadHistorial]);

  // Refs para que el listener de postMessage (montado una sola vez) siempre
  // lea el estado más reciente de "¿está abierto el historial?".
  const histOpenRef = useRef(histOpen);
  const loadHistorialRef = useRef(loadHistorial);
  useEffect(() => { histOpenRef.current = histOpen; }, [histOpen]);
  useEffect(() => { loadHistorialRef.current = loadHistorial; }, [loadHistorial]);

  useEffect(() => {
    function onMsg(e) {
      if (!e.data || e.data.type !== 'sgv_doc' || e.data.docType !== 'prejob_segavan') return;
      const d = e.data;
      api.post('/api/documents/create', {
        tipo: 'prejob',
        nombre: d.nombre || 'Prejob Segavan',
        año: new Date().getFullYear(),
        semana: null,
        total_gastos: 0,
        nota_numero: null,
        file_b64: d.fileB64 || null,
        file_name: d.fileName || 'prejob-segavan.html',
        parte_data: d.parteData || null,
      }).then(() => { if (histOpenRef.current) loadHistorialRef.current(); }).catch(() => {});
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  async function eliminarDoc(d) {
    if (!window.confirm(`¿Eliminar "${d.nombre || 'este prejob'}" del historial?`)) return;
    try {
      await api.delete(`/api/documents/${d.id}`);
      setDocs(prev => prev.filter(x => x.id !== d.id));
    } catch (e) { alert(e.response?.data?.error || e.message || 'No se pudo eliminar'); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface2)', flexShrink: 0 }}>
        <button
          onClick={() => setHistOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px',
            fontSize: 12, fontWeight: 700, color: 'var(--text)',
          }}
        >
          <span>📜 Historial de prejobs en los que apareces</span>
          <span style={{ marginLeft: 'auto', color: 'var(--text-dim)', fontSize: 11, transform: histOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>▼</span>
        </button>
        {histOpen && (
          <div style={{ padding: '0 14px 12px', maxHeight: 300, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ color: 'var(--text-dim)', fontSize: 12, padding: '8px 0' }}>Cargando…</div>
            ) : docs.length === 0 ? (
              <div style={{ color: 'var(--text-dim)', fontSize: 12, padding: '8px 0' }}>No apareces en ningún prejob todavía.</div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {docs.map(d => {
                  const pd = d.parte_data || {};
                  const open = openId === d.id;
                  const canDelete = isAdmin || d.user_id === user?.id;
                  return (
                    <div key={d.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
                      <div
                        onClick={() => setOpenId(open ? null : d.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer', flexWrap: 'wrap' }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{d.nombre || 'Prejob Segavan'}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{fmtFecha(pd.fecha || d.created_at)}</span>
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                          <button onClick={e => { e.stopPropagation(); verDoc(d); }} title="Ver / imprimir"
                            style={{ background: 'rgba(96,165,250,.1)', border: '1px solid rgba(96,165,250,.3)', color: '#60a5fa', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', fontSize: 12, lineHeight: 1 }}>
                            👁
                          </button>
                          <button onClick={e => { e.stopPropagation(); descargarDoc(d); }} title="Descargar"
                            style={{ background: 'rgba(74,222,128,.1)', border: '1px solid rgba(74,222,128,.3)', color: '#4ade80', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', fontSize: 12, lineHeight: 1 }}>
                            ⬇
                          </button>
                          {canDelete && (
                            <button onClick={e => { e.stopPropagation(); eliminarDoc(d); }} title="Eliminar"
                              style={{ background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)', color: '#f87171', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', fontSize: 12, lineHeight: 1 }}>
                              🗑
                            </button>
                          )}
                          <span style={{ color: 'var(--text-dim)', fontSize: 11, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}>›</span>
                        </div>
                      </div>
                      {open && (
                        <div style={{ padding: '0 10px 10px', fontSize: 11, color: 'var(--text-dim)' }}>
                          {pd.instalacion && <div style={{ marginBottom: 4 }}>Instalación: <span style={{ color: 'var(--text)' }}>{pd.instalacion}</span></div>}
                          {Array.isArray(pd.personas) && pd.personas.length > 0 && (
                            <div>
                              Personal en obra:
                              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                                {pd.personas.map((p, i) => (
                                  <li key={i} style={{ color: 'var(--text)' }}>{p.nombre}{p.dni ? ` · ${p.dni}` : ''}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 8, fontStyle: 'italic' }}>
              Los prejobs se eliminan automáticamente a la semana de creados.
            </div>
          </div>
        )}
      </div>
      <iframe
        src="/prejob-segavan.html"
        title="Prejob Segavan"
        style={{ width: '100%', flex: 1, border: 'none', display: 'block' }}
      />
    </div>
  );
}
